import type { ReactNode } from 'react';

/**
 * MetadataItem - Individual metadata field display component
 *
 * Features:
 * - Icon + label + value layout
 * - Responsive text handling with word-break
 * - Consistent spacing and typography
 * - Accessible color contrast
 *
 * Accessibility:
 * - Uses semantic HTML structure
 * - Clear visual hierarchy with labels
 */
export interface MetadataItemProps {
  /** Icon element to display */
  icon: ReactNode;
  /** Field label (e.g., "World Name") */
  label: string;
  /** Field value. When href is provided, prefer passing a string for correct link text rendering. */
  value: string | ReactNode;
  /** Optional URL to make the value a link (opens in new tab). Must be an https:// URL. */
  href?: string;
}

export function MetadataItem({ icon, label, value, href }: MetadataItemProps) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="text-muted-foreground mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
        <p className="text-sm text-foreground break-words">
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {value}
            </a>
          ) : (
            value
          )}
        </p>
      </div>
    </div>
  );
}
